import type { Hotkeys } from 'owa-hotkeys';
import { isMac } from 'owa-user-agent/lib/userAgent';

// Source of truth for the two platform-specific Copilot DAB keyboard values:
//
// Three representations are needed for the three use cases:
// 1. Displaying the hotkey string in the UI = `getHotkey`
// 2. Displaying the hotkey string in the `aria-keyshortcuts` attribute = `getAriaKeyShortcuts`
// 3. Verifying the hotkey has been input = `matchesHotkey`

type KeyCombinations = {
    hotkey: Hotkeys;
    shortcut: string;
};

const mac: KeyCombinations = {
    hotkey: '⌘+ctrl+i',
    shortcut: 'Meta+Control+I',
};

const windows: KeyCombinations = {
    hotkey: 'alt+c',
    shortcut: 'Alt+C',
};

/**
 * Returns the Copilot DAB shortcut as a string to display in the UI (ie: "Shortcuts" dialog)
 */
export const getHotkey = (): Hotkeys => {
    return isMac() ? mac.hotkey : windows.hotkey;
};

/** Returns the `aria-keyshortcuts` attribute */
export const getAriaKeyShortcuts = (): { 'aria-keyshortcuts': string } => {
    return {
        'aria-keyshortcuts': isMac() ? mac.shortcut : windows.shortcut,
    };
};

/**
 * Returns `true` when the provided `KeyboardEvent` matches the Copilot DAB shortcut
 */
export const matchesHotkey = (event: KeyboardEvent): boolean => {
    const { altKey, code, ctrlKey, metaKey, shiftKey } = event;

    const macMatches = metaKey && ctrlKey && code === 'KeyI' && !altKey && !shiftKey;
    const windowsMatches = altKey && code === 'KeyC' && !ctrlKey && !metaKey && !shiftKey;

    return isMac() ? macMatches : windowsMatches;
};
